import { useState, useEffect, useRef } from "react";
import { thumbUrl, videoUrl } from "../utils/imageUpload.js";

const isVideoUrl = url => /\.(mp4|mov|webm|m3u8)(\?|$)/i.test(url || "");

// ─── MODAL IMAGE SWIPER ───────────────────────────────────────────────────────
// Swipeable image viewer for product modals — self-contained, manages its own
// index + drag animation. Resets to frame 0 when the images array changes.
//
// Props:
//   images[]  — array of image/video URLs
//   style{}   — applied to the image container (set width, borderRadius, background etc.). The container's
//               height follows the CURRENT photo's own aspect ratio (see below), so don't set a height.
//   dark      — bool, use light dots (for dark modal backgrounds)
// Every image is a real <img> in the DOM at once (loading="lazy" past index 0 — the browser's
// own lazy-loading handles fetch timing), in a flex track whose slide position is a single
// transform driven purely by React state (idx + live drag offset), animated with a CSS
// transition. This replaces an earlier version that kept ONE <img> and manually choreographed
// each swipe through a 5-step chain of setTimeout/requestAnimationFrame calls, imperatively
// mutating a ref's DOM style at each step — that chain could be abandoned mid-sequence (a
// re-render, a second swipe arriving before the first settled, the product changing mid-swipe)
// and leave the track's transform, or the animLock guard blocking all further swipes, stuck in
// a partial state. A transform that's always a pure function of current state can't get stuck.
// Shape of the photo, kept within sane bounds (a 1:5 sliver or a panorama shouldn't make the viewer absurd).
const ratioOf = (w, h) => (w > 0 && h > 0 ? Math.min(3, Math.max(0.35, w / h)) : null);
const MAX_VIEWER_HEIGHT = "78vh"; // taller photos are shown whole (letterboxed), never cropped or scrolled

export function ModalImageSwiper({ images = [], style = {}, dark = false }) {
  const count = images.length;
  const [idx, setIdx]     = useState(0);
  // Natural aspect ratio (w/h) of each photo, learned as it loads. The viewer's height follows the photo
  // currently showing, so customers see the WHOLE picture at its real shape — not a square crop.
  const [ratios, setRatios] = useState({});
  const noteRatio = (i, w, h) => { const r = ratioOf(w, h); if (r) setRatios((p) => (p[i] === r ? p : { ...p, [i]: r })); };
  const [dragPx, setDragPx] = useState(0);
  const [snapping, setSnapping] = useState(false);
  const containerRef      = useRef(null);
  const startX            = useRef(null);
  const startY            = useRef(null);

  const goTo = (i) => { setSnapping(true); setIdx(Math.max(0, Math.min(count - 1, i))); setDragPx(0); setTimeout(() => setSnapping(false), 280); };

  // Reset to first frame whenever a new product is shown
  useEffect(() => { setIdx(0); setDragPx(0); setSnapping(false); setRatios({}); }, [images]); // eslint-disable-line react-hooks/exhaustive-deps

  // Non-passive touchmove is required to preventDefault() the page's own vertical scroll while
  // swiping horizontally — React's JSX touch handlers are passive by default and can't do this.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || count <= 1) return;
    const onStart = (e) => { startX.current = e.touches[0].clientX; startY.current = e.touches[0].clientY; setSnapping(false); };
    const onMove = (e) => {
      if (startX.current === null) return;
      const dx = e.touches[0].clientX - startX.current;
      const dy = Math.abs(e.touches[0].clientY - startY.current);
      if (Math.abs(dx) > dy && Math.abs(dx) > 6) {
        e.preventDefault();
        const atEdge = (dx > 0 && idx === 0) || (dx < 0 && idx === count - 1);
        setDragPx(atEdge ? dx * 0.2 : dx * 0.9);
      }
    };
    const onEnd = (e) => {
      if (startX.current === null) return;
      const delta = e.changedTouches[0].clientX - startX.current;
      startX.current = null; startY.current = null;
      if (Math.abs(delta) > 45 && ((delta < 0 && idx < count - 1) || (delta > 0 && idx > 0))) { goTo(idx + (delta < 0 ? 1 : -1)); }
      else { setSnapping(true); setDragPx(0); setTimeout(() => setSnapping(false), 280); } // rubber-band back at first/last, or below threshold
    };
    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", onEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
    };
  }, [count, idx]);

  if (!count) return null;

  const dotActive   = "#2F63DD";
  const dotInactive = dark ? "rgba(255,255,255,0.22)" : "rgba(0,0,0,0.16)";

  return (
    <div style={{ position:"relative", userSelect:"none" }}>
      {/* ── Main image / video ── */}
      {/* Every photo sits in one flex row (what keeps swiping reliable), so the row has ONE height: the
          container takes the shape of the photo currently showing, and each photo is drawn with
          object-fit: contain inside it — whole picture, never cropped. A photo that's wider/taller than the
          current one (mid-swipe) is letterboxed for that moment, then the container reshapes to it. */}
      <div ref={containerRef} style={{ background: dark ? "#111" : "var(--surface, #f5f5f5)", ...style, aspectRatio: String(ratios[idx] ?? ratios[0] ?? 1), maxHeight: MAX_VIEWER_HEIGHT, transition:"aspect-ratio 0.25s ease", position:"relative", overflow:"hidden" }}>
        {count > 0 && (
          <div style={{
            display:"flex", width:`${count * 100}%`, height:"100%",
            transform:`translateX(calc((${-idx * 100}% + ${dragPx}px) / ${count}))`,
            transition: snapping ? "transform 0.28s cubic-bezier(0.25,0.46,0.45,0.94)" : "none",
          }}>
            {images.map((s, i) => (
              <div key={i} style={{ width:`${100 / count}%`, flexShrink:0, height:"100%" }}>
                {isVideoUrl(s)
                  ? (i === idx ? <video src={videoUrl(s)} autoPlay muted loop playsInline preload="metadata" onLoadedMetadata={(e) => noteRatio(i, e.currentTarget.videoWidth, e.currentTarget.videoHeight)} style={{ width:"100%", height:"100%", objectFit:"contain", display:"block" }}/> : <div style={{ width:"100%", height:"100%", background:"#111" }}/>)
                  : <img src={thumbUrl(s, 1200)} alt="" draggable={false} loading={i === 0 ? "eager" : "lazy"}
                        ref={(el) => { if (el && el.complete && el.naturalWidth) noteRatio(i, el.naturalWidth, el.naturalHeight); }}
                        onLoad={(e) => noteRatio(i, e.currentTarget.naturalWidth, e.currentTarget.naturalHeight)}
                        style={{ width:"100%", height:"100%", objectFit:"contain", display:"block" }}/>}
              </div>
            ))}
          </div>
        )}

        {/* ── Overlay arrows (desktop) ── */}
        {count > 1 && idx > 0 && (
          <button onClick={(e) => { e.stopPropagation(); goTo(idx - 1); }}
            style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)",
              background:"rgba(0,0,0,0.4)", backdropFilter:"blur(4px)", border:"none",
              borderRadius:"50%", width:34, height:34, cursor:"pointer", color:"#fff",
              fontSize:22, display:"flex", alignItems:"center", justifyContent:"center",
              zIndex:5, lineHeight:1 }}>‹</button>
        )}
        {count > 1 && idx < count - 1 && (
          <button onClick={(e) => { e.stopPropagation(); goTo(idx + 1); }}
            style={{ position:"absolute", right:10, top:"50%", transform:"translateY(-50%)",
              background:"rgba(0,0,0,0.4)", backdropFilter:"blur(4px)", border:"none",
              borderRadius:"50%", width:34, height:34, cursor:"pointer", color:"#fff",
              fontSize:22, display:"flex", alignItems:"center", justifyContent:"center",
              zIndex:5, lineHeight:1 }}>›</button>
        )}

        {/* ── Index counter (top-right) — only when 3+ images ── */}
        {count >= 3 && (
          <div style={{ position:"absolute", top:10, right:10, background:"rgba(0,0,0,0.45)",
            backdropFilter:"blur(4px)", borderRadius:4, padding:"3px 10px",
            fontSize:11, fontWeight:700, color:"#fff", zIndex:5, letterSpacing:"0.04em" }}>
            {idx + 1} / {count}
          </div>
        )}
      </div>

      {/* ── Pill dot indicators ── */}
      {count > 1 && (
        <div style={{ display:"flex", justifyContent:"center", alignItems:"center", gap:5, padding:"8px 0 2px" }}>
          {images.map((_, i) => (
            <button key={i} onClick={() => goTo(i)}
              style={{ width: i === idx ? 18 : 6, height:6, borderRadius:3, border:"none", padding:0,
                cursor:"pointer", background: i === idx ? dotActive : dotInactive,
                transition:"background 0.2s, width 0.22s cubic-bezier(0.34,1.56,0.64,1)" }}/>
          ))}
        </div>
      )}
    </div>
  );
}
